import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
	Typography,
	Box,
	Grid,
	Paper,
	TextField,
	Button,
	Fade,
	CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import HomeIcon from '@mui/icons-material/Home';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(4),
		marginTop: theme.spacing(3),
		borderRadius: '20px',
		backgroundImage: 'linear-gradient(120deg, #ffffff 0%, #f5f7fa 100%)',
		boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
		overflow: 'hidden',
		position: 'relative',
		width: '100%',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '6px',
			background: 'linear-gradient(90deg, #004F2B, #007542)'
		}
	},
	firstTitle: {
		textAlign: 'center',
		fontWeight: 600,
		marginBottom: theme.spacing(3),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
		color: '#2d3748',
		fontSize: '1.8rem',
		letterSpacing: '0.5px',
		textTransform: 'uppercase',
		background: 'black',
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent'
	},
	featuresList: {
		width: '100%',
		margin: '0 auto',
		marginBottom: theme.spacing(4)
	},
	featureItem: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.7rem',
		marginBottom: '8px'
	},
	featureIcon: {
		color: '#004F2B',
		fontSize: '1.4rem',
		transition: 'transform 0.3s ease'
	},
	featureText: {
		fontSize: '1rem',
		fontWeight: 500,
		color: '#637381'
	},
	paperWrapper: {
		borderRadius: '20px',
		boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		transition: 'all 0.3s ease',
		background: 'rgba(255, 255, 255, 0.8)',
		backdropFilter: 'blur(10px)',
		'&:hover': {
			boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
		}
	},
	formField: {
		padding: '1rem',
		marginBottom: '1rem',
		borderRadius: '16px',
		backgroundColor: 'white',
		transition: 'all 0.3s ease',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		overflow: 'hidden',
		boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)',
		'&:hover': {
			transform: 'translateY(-5px)',
			boxShadow: '0 10px 25px rgba(0, 79, 43, 0.15)'
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			width: '5px',
			height: '100%',
			background: 'linear-gradient(to bottom, #004F2B, #007542)',
			borderTopLeftRadius: '16px',
			borderBottomLeftRadius: '16px'
		}
	},
	fieldLabel: {
		fontSize: '1rem',
		fontWeight: 500,
		color: '#637381',
		display: 'flex',
		alignItems: 'center',
		gap: '0.7rem',
		marginBottom: '0.5rem'
	},
	fieldIcon: {
		color: '#004F2B',
		fontSize: '1.4rem',
		transition: 'transform 0.3s ease'
	},
	textField: {
		width: '100%',
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				borderColor: '#004F2B',
				borderRadius: '10px'
			},
			'&:hover fieldset': {
				borderColor: '#007542'
			},
			'&.Mui-focused fieldset': {
				borderColor: '#004F2B'
			}
		}
	},
	submitButton: {
		backgroundColor: '#004F2B',
		color: 'white',
		padding: '12px 30px',
		borderRadius: '30px',
		fontSize: '1rem',
		fontWeight: 600,
		textTransform: 'none',
		boxShadow: '0 4px 10px rgba(0, 79, 43, 0.2)',
		transition: 'all 0.3s ease',
		'&:hover': {
			backgroundColor: '#007542',
			boxShadow: '0 6px 15px rgba(0, 79, 43, 0.3)',
			transform: 'translateY(-2px)'
		}
	},
	successMessage: {
		padding: theme.spacing(4),
		textAlign: 'center'
	},
	successTitle: {
		color: '#004F2B',
		fontWeight: 700,
		marginBottom: theme.spacing(2)
	},
	successText: {
		color: '#637381',
		fontSize: '1.1rem'
	}
}));

const validateName = (value) => {
	const nameRegex = /^[a-zA-Z\s'-]+$/;
	return nameRegex.test(value) || 'Invalid name format';
};

const validatePhoneNumber = (value) => {
	const phoneRegex = /^(?:\(\d{3}\)\s?|^\d{3}[-\s]?)\d{3}[-\s]?\d{4}$/;
	return phoneRegex.test(value) || 'Invalid phone number format';
};

const validateEmail = (value) => {
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	return emailRegex.test(value) || 'Invalid email address format';
};

export default function LeadCaptureForm({
	address,
	city,
	onSubmit,
	submissionSuccess,
	errors,
	control
}) {
	const classes = useStyles();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const renderFeaturesList = () => {
		const features = [
			{
				text: 'Personalized, Free Real Estate Consultation',
				icon: <HomeIcon className={classes.featureIcon} />
			},
			{
				text: 'Expert Market Analysis',
				icon: <AssessmentIcon className={classes.featureIcon} />
			},
			{
				text: 'Property Valuation',
				icon: <SearchIcon className={classes.featureIcon} />
			}
		];

		return features.map((feature, index) => (
			<Fade in={true} style={{ transitionDelay: `${index * 100}ms` }} key={feature.text}>
				<div className={classes.featureItem}>
					{feature.icon}
					<Typography className={classes.featureText}>{feature.text}</Typography>
				</div>
			</Fade>
		));
	};

	const handleSubmit = (e) => {
		setIsSubmitting(true);
		onSubmit(e);
	};

	return (
		<Box className={classes.root}>
			<Typography variant="h3" component="h3" className={classes.firstTitle}>
				<span style={{ flexGrow: 1 }}>Contact Us</span>
			</Typography>

			<Box style={{ padding: '0rem' }}>
				{!submissionSuccess ? (
					<>
						<Box mb={4}>
							<div className={classes.featuresList}>{renderFeaturesList()}</div>
						</Box>

						<form onSubmit={handleSubmit}>
							<Grid container spacing={3}>
								{!address && (
									<>
										<Grid item xs={12}>
											<Typography
												variant="h6"
												style={{ marginBottom: '10px' }}
											>
												Address Details:
											</Typography>
										</Grid>
										<Grid item xs={12} sm={6}>
											<Fade in={true} style={{ transitionDelay: '100ms' }}>
												<Box className={classes.formField}>
													<Typography className={classes.fieldLabel}>
														<HomeIcon className={classes.fieldIcon} />
														Street Address
													</Typography>
													<Controller
														name="address"
														control={control}
														defaultValue=""
														render={({ field }) => (
															<TextField
																id="addressValue"
																className={classes.textField}
																required
																value={field.value}
																variant="outlined"
																placeholder="Enter property address"
																onChange={(e) =>
																	field.onChange(e.target.value)
																}
															/>
														)}
													/>
												</Box>
											</Fade>
										</Grid>
										{!city && (
											<Grid item xs={12} sm={6}>
												<Fade
													in={true}
													style={{ transitionDelay: '200ms' }}
												>
													<Box className={classes.formField}>
														<Typography className={classes.fieldLabel}>
															<HomeIcon
																className={classes.fieldIcon}
															/>
															City
														</Typography>
														<Controller
															name="city"
															control={control}
															defaultValue=""
															render={({ field }) => (
																<TextField
																	id="cityInformation"
																	className={classes.textField}
																	required
																	value={field.value}
																	variant="outlined"
																	placeholder="Enter city"
																	onChange={(e) =>
																		field.onChange(
																			e.target.value
																		)
																	}
																/>
															)}
														/>
													</Box>
												</Fade>
											</Grid>
										)}
									</>
								)}

								<Grid item xs={12} sm={6}>
									<Fade in={true} style={{ transitionDelay: '300ms' }}>
										<Box className={classes.formField}>
											<Typography className={classes.fieldLabel}>
												<PersonIcon className={classes.fieldIcon} />
												Name
											</Typography>
											<Controller
												name="name"
												control={control}
												defaultValue=""
												rules={{ validate: validateName }}
												render={({ field }) => (
													<TextField
														id="name"
														className={classes.textField}
														required
														error={!!errors.name}
														helperText={
															errors.name ? errors.name.message : ''
														}
														value={field.value}
														variant="outlined"
														placeholder="Your full name"
														onChange={(e) =>
															field.onChange(e.target.value)
														}
													/>
												)}
											/>
										</Box>
									</Fade>
								</Grid>

								<Grid item xs={12} sm={6}>
									<Fade in={true} style={{ transitionDelay: '400ms' }}>
										<Box className={classes.formField}>
											<Typography className={classes.fieldLabel}>
												<EmailIcon className={classes.fieldIcon} />
												Email Address
											</Typography>
											<Controller
												name="emailAddress"
												control={control}
												defaultValue=""
												rules={{ validate: validateEmail }}
												render={({ field }) => (
													<TextField
														id="emailAddress"
														className={classes.textField}
														required
														error={!!errors.emailAddress}
														helperText={
															errors.emailAddress
																? errors.emailAddress.message
																: ''
														}
														value={field.value}
														variant="outlined"
														placeholder="your.email@example.com"
														onChange={(e) =>
															field.onChange(e.target.value)
														}
													/>
												)}
											/>
										</Box>
									</Fade>
								</Grid>

								<Grid item xs={12} sm={6}>
									<Fade in={true} style={{ transitionDelay: '500ms' }}>
										<Box className={classes.formField}>
											<Typography className={classes.fieldLabel}>
												<PhoneIcon className={classes.fieldIcon} />
												Phone Number (optional)
											</Typography>
											<Controller
												name="phoneNumber"
												control={control}
												defaultValue=""
												rules={{
													validate: (value) =>
														value === '' || validatePhoneNumber(value)
												}}
												render={({ field }) => (
													<TextField
														id="phoneNumber"
														className={classes.textField}
														error={!!errors.phoneNumber}
														helperText={
															errors.phoneNumber
																? errors.phoneNumber.message
																: ''
														}
														value={field.value}
														variant="outlined"
														placeholder="(123) 456-7890"
														onChange={(e) =>
															field.onChange(e.target.value)
														}
													/>
												)}
											/>
										</Box>
									</Fade>
								</Grid>

								<Grid
									item
									xs={12}
									style={{ textAlign: 'center', marginTop: '20px' }}
								>
									<Fade in={true} style={{ transitionDelay: '600ms' }}>
										<Button
											type="submit"
											variant="contained"
											className={classes.submitButton}
											disabled={isSubmitting}
										>
											{isSubmitting ? (
												<>
													<CircularProgress
														size={20}
														color="inherit"
														style={{ marginRight: 10 }}
													/>
													Submitting...
												</>
											) : (
												'Contact Us Now'
											)}
										</Button>
									</Fade>
								</Grid>
							</Grid>
						</form>
					</>
				) : (
					<Fade in={true}>
						<Box className={classes.successMessage}>
							<Typography variant="h5" className={classes.successTitle}>
								Thank you for contacting us!
							</Typography>
							<Typography variant="body1" className={classes.successText}>
								Your inquiry has been received. We'll be in touch with you shortly.
							</Typography>
						</Box>
					</Fade>
				)}
			</Box>
		</Box>
	);
}
